import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft, Mail, Home } from "lucide-react";

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy Policy — Ujjain Maha Kumbh 2028";
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "April 20, 2026";
  const contactEmail = "contact@ujjainmahakumbh.com";
  const siteUrl = "https://ujjainmahakumbh.com";

  return (
    <div className="min-h-screen bg-[#1C1C1E] text-white" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        {/* Header */}
        <div className="mb-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#FF9933] transition-colors mb-6"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF9933] to-[#E67300] flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
            <h1
              className="text-3xl sm:text-4xl font-bold text-white"
              style={{ fontFamily: "Eczar, serif" }}
            >
              Privacy Policy
            </h1>
          </div>
          <p className="text-sm text-white/50">
            Last updated: <span className="text-white/80">{lastUpdated}</span>
          </p>
        </div>

        {/* Legal notice banner */}
        <div className="mb-10 p-4 rounded-lg bg-amber-950/40 border border-amber-800/60 text-amber-200 text-sm">
          <strong className="font-semibold">Important:</strong> This website and mobile app
          (ujjainmahakumbh.com) is a <strong>community volunteer initiative</strong> and is{" "}
          <strong>NOT an official website of the Government of Madhya Pradesh</strong> or any
          government agency. For official Kumbh Mela registrations and announcements, please visit
          the MP Government's official Kumbh portal.
        </div>

        {/* Policy sections */}
        <div className="space-y-8 prose-custom">
          <Section title="1. Introduction">
            <p>
              Welcome to Ujjain Maha Kumbh 2028 (referred to as "we," "our," or "us"). We are
              committed to protecting your personal information and your right to privacy. This
              Privacy Policy explains what information we collect, how we use it, and what rights
              you have regarding it.
            </p>
            <p>
              This policy applies to our website{" "}
              <a href={siteUrl} className="text-[#FF9933] underline">
                {siteUrl}
              </a>{" "}
              and our official Android mobile application ("Ujjain Maha Kumbh", published on Google
              Play Store by DeveloperZone).
            </p>
          </Section>

          <Section title="2. Information We Collect">
            <p>When you register on our portal as a pilgrim, volunteer, vendor, or media person, we collect the following information:</p>
            <ul>
              <li><strong>Full name</strong> — to identify and address you</li>
              <li><strong>Email address</strong> — to send registration confirmation and event updates</li>
              <li><strong>Phone number</strong> — for logistical coordination if you attend the event</li>
              <li><strong>State of residence</strong> — for travel and arrangement planning</li>
              <li><strong>Category</strong> — whether you are registering as a pilgrim, volunteer, vendor, or media representative</li>
              <li><strong>Number of pilgrims</strong> (for pilgrim category) — to estimate group size</li>
              <li><strong>Skills</strong> (for volunteers) — to match you with appropriate roles</li>
              <li><strong>Organization</strong> (for vendors/media) — for verification</li>
              <li><strong>Special needs, messages, and notes</strong> — to accommodate your requirements</li>
            </ul>
            <p>
              We also automatically collect basic technical data (IP address, browser type, device
              type, pages visited) solely for security, debugging, and anonymous usage analytics.
            </p>
            <p>
              <strong>We do NOT collect:</strong> Aadhaar numbers, government IDs, financial
              information, credit card data, precise GPS location, photos, videos, audio
              recordings, or contacts list.
            </p>
          </Section>

          <Section title="3. How We Use Your Information">
            <p>Your information is used exclusively for:</p>
            <ul>
              <li>Sending you a <strong>registration confirmation email</strong></li>
              <li>Sending <strong>important updates</strong> about the event, such as Shahi Snan (royal bath) dates, travel guidelines, safety announcements, and volunteer assignments</li>
              <li>Coordinating <strong>on-ground logistics</strong> for pilgrims, volunteers, and vendors</li>
              <li>Responding to your <strong>queries and messages</strong></li>
              <li>Improving our website and services based on <strong>anonymous usage patterns</strong></li>
            </ul>
          </Section>

          <Section title="4. How We Do NOT Use Your Information">
            <ul>
              <li>We <strong>never sell</strong> your personal information</li>
              <li>We <strong>never rent or trade</strong> your information with advertisers, marketers, or data brokers</li>
              <li>We <strong>do not share</strong> your information with any third party except as described in Section 5 below</li>
              <li>We <strong>do not use</strong> your information for behavioral targeting or profiling</li>
              <li>We <strong>do not send</strong> you unsolicited promotional emails from third parties</li>
            </ul>
          </Section>

          <Section title="5. Information Sharing">
            <p>Your personal information is shared only in the following strictly limited circumstances:</p>
            <ul>
              <li><strong>Email service provider:</strong> We use Gmail SMTP to deliver confirmation and broadcast emails. Google processes the email envelope (sender, recipient, subject) as needed for delivery.</li>
              <li><strong>Hosting provider:</strong> Our website and database are hosted on secure cloud infrastructure. The provider has access to encrypted data at rest but cannot read the contents in meaningful form.</li>
              <li><strong>Legal compliance:</strong> If required by a valid court order or legal process under Indian law, we may share information with authorized government authorities.</li>
              <li><strong>Event coordination (opt-in only):</strong> If you volunteer, your contact info may be shared with on-ground coordinators for your specific assignment — only after explicit confirmation from you.</li>
            </ul>
          </Section>

          <Section title="6. Your Rights">
            <p>Under Indian law (Digital Personal Data Protection Act 2023) and international privacy standards (GDPR, CCPA), you have the following rights:</p>
            <ul>
              <li><strong>Right to access:</strong> Request a copy of all personal data we hold about you</li>
              <li><strong>Right to rectification:</strong> Ask us to correct any inaccurate information</li>
              <li><strong>Right to erasure ("right to be forgotten"):</strong> Request deletion of your data at any time</li>
              <li><strong>Right to withdraw consent:</strong> Opt out of future communications</li>
              <li><strong>Right to data portability:</strong> Receive your data in a machine-readable format</li>
              <li><strong>Right to lodge a complaint:</strong> With the Data Protection Board of India or any relevant authority</li>
            </ul>
            <p>
              To exercise any of these rights, email us at{" "}
              <a href={`mailto:${contactEmail}`} className="text-[#FF9933] underline">
                {contactEmail}
              </a>
              . We respond within 7 business days.
            </p>
          </Section>

          <Section title="7. Data Retention">
            <p>
              We retain your registration information until the Kumbh Mela 2028 event concludes
              (approximately May 2028) plus <strong>1 additional year</strong> for follow-up
              communications. After that, your records are permanently deleted from our active
              database.
            </p>
            <p>
              You may request earlier deletion at any time by emailing{" "}
              <a href={`mailto:${contactEmail}`} className="text-[#FF9933] underline">
                {contactEmail}
              </a>
              .
            </p>
          </Section>

          <Section title="8. Data Security">
            <p>We implement industry-standard security measures:</p>
            <ul>
              <li>HTTPS (TLS 1.2+) encryption for all data in transit</li>
              <li>Passwords hashed using bcrypt</li>
              <li>Admin access protected by JWT tokens with 24-hour expiry</li>
              <li>Database access restricted to authorized administrators only</li>
              <li>Regular automated backups stored in encrypted form</li>
            </ul>
            <p>
              However, no system is 100% secure. If we detect a breach affecting your data, we will
              notify you within 72 hours via email as required by law.
            </p>
          </Section>

          <Section title="9. Children's Privacy">
            <p>
              Our services are not directed to children under 13. We do not knowingly collect
              information from anyone under 13. Parents registering on behalf of minors under 18
              should include the minor's information only in the "Number of pilgrims" field or
              "Message" section — not create separate registrations for them.
            </p>
          </Section>

          <Section title="10. Cookies & Tracking">
            <p>
              Our website uses minimal <strong>essential cookies</strong> only — for session
              management and basic functionality. We do NOT use third-party advertising cookies,
              Google Analytics, Facebook Pixel, or any behavioral tracking tools.
            </p>
          </Section>

          <Section title="11. Mobile App Permissions">
            <p>The "Ujjain Maha Kumbh" Android app requests minimal permissions:</p>
            <ul>
              <li><strong>Internet access</strong> — to connect to our backend API (required for all features)</li>
              <li><strong>Network state</strong> — to detect when you are offline and cache accordingly</li>
            </ul>
            <p>
              The app does <strong>NOT</strong> request access to your camera, microphone, contacts,
              SMS, calendar, location, storage (beyond app-private), or any other sensitive
              permission.
            </p>
          </Section>

          <Section title="12. Third-Party Services">
            <p>We use these third-party services with strict data-minimization:</p>
            <ul>
              <li><strong>Google Places API</strong> (via Pica Passthrough) — for location search on the Locations page. No personal data is sent; only your anonymous search query.</li>
              <li><strong>Google Gmail SMTP</strong> — for outbound email delivery. Handles recipient address and email content only.</li>
              <li><strong>MongoDB Atlas / self-hosted MongoDB</strong> — database hosting.</li>
            </ul>
          </Section>

          <Section title="13. International Users">
            <p>
              Our services are primarily intended for Indian residents and pilgrims traveling to
              Ujjain, India. If you access our services from outside India, your data will be
              processed and stored in India (or cloud data centers serving the India region).
            </p>
          </Section>

          <Section title="14. Changes to This Policy">
            <p>
              We may update this Privacy Policy occasionally to reflect legal changes or new
              features. When we do, we'll update the "Last updated" date at the top. For material
              changes, we'll notify registered users via email at least 7 days before the change
              takes effect.
            </p>
          </Section>

          <Section title="15. Contact Us">
            <p>For any privacy questions, data requests, or concerns, please contact:</p>
            <div className="p-4 rounded-lg bg-white/5 border border-white/10">
              <p className="flex items-center gap-2 m-0 text-white/90">
                <Mail className="w-4 h-4 text-[#FF9933]" />
                <a
                  href={`mailto:${contactEmail}`}
                  className="text-[#FF9933] underline font-mono text-sm"
                  data-testid="contact-email-link"
                >
                  {contactEmail}
                </a>
              </p>
              <p className="text-sm text-white/60 mt-2 mb-0">
                Response time: Within 7 business days
              </p>
            </div>
          </Section>
        </div>

        {/* Footer back link */}
        <div className="mt-12 pt-8 border-t border-white/10 flex items-center justify-between flex-wrap gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm bg-[#FF9933] hover:bg-[#E67300] text-white rounded-lg font-semibold transition-colors"
            data-testid="footer-home-link"
          >
            <Home className="w-4 h-4" />
            Back to Home
          </Link>
          <p className="text-xs text-white/40">
            Har Har Mahadev · Community Volunteer Initiative
          </p>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-3">
      <h2
        className="text-xl font-semibold text-[#FF9933]"
        style={{ fontFamily: "Eczar, serif" }}
      >
        {title}
      </h2>
      <div className="text-sm text-white/75 leading-relaxed space-y-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 [&_a]:text-[#FF9933] [&_a]:hover:underline [&_strong]:text-white">
        {children}
      </div>
    </section>
  );
}
